import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../hooks/useApi';
import {
  ArrowLeftIcon, SendIcon, MessageIcon, LockIcon, AlertIcon,
  CheckIcon, ClockIcon,
} from '../../components/Icons';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ESTADOS_LABEL = {
  pendente:   'Pendente',
  confirmada: 'Confirmada',
  entregue:   'Entregue',
  em_uso:     'Em uso',
  devolvida:  'Devolvida',
  concluida:  'Concluída',
  cancelada:  'Cancelada',
  disputa:    'Em disputa',
};

function formatHour(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-AO', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso) {
  const d = new Date(iso);
  const hoje = new Date();
  const ontem = new Date(hoje); ontem.setDate(hoje.getDate() - 1);
  if (d.toDateString() === hoje.toDateString()) return 'Hoje';
  if (d.toDateString() === ontem.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-AO', { day: '2-digit', month: 'short' });
}

function groupByDay(msgs) {
  const groups = [];
  let lastDay = null;
  msgs.forEach(m => {
    const day = new Date(m.created_at).toDateString();
    if (day !== lastDay) {
      groups.push({ type: 'separator', label: formatDate(m.created_at), id: `sep-${m.id}` });
      lastDay = day;
    }
    groups.push(m);
  });
  return groups;
}

export default function Chat() {
  const { reserva_id } = useParams();
  const navigate = useNavigate();
  const { utilizador } = useAuth();

  const [mensagens,    setMensagens]    = useState([]);
  const [outraParte,   setOutraParte]   = useState(null);
  const [anuncioTitulo,setAnuncioTitulo]= useState('');
  const [chatActivo,   setChatActivo]   = useState(false);
  const [estado,       setEstado]       = useState('');
  const [texto,        setTexto]        = useState('');
  const [enviando,     setEnviando]     = useState(false);
  const [erro,         setErro]         = useState('');
  const [carregando,   setCarregando]   = useState(true);

  const fimRef    = useRef(null);
  const inputRef  = useRef(null);
  const pollRef   = useRef(null);

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    try {
      const data = await apiFetch(`/chat/${reserva_id}`);
      setMensagens(data.mensagens || []);
      setChatActivo(data.chat_activo);
      setEstado(data.estado);
      setAnuncioTitulo(data.anuncio_titulo || '');
      setOutraParte(data.outra_parte || null);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar conversa');
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }, [reserva_id]);

  // Polling a cada 5 segundos enquanto o chat estiver activo
  useEffect(() => {
    carregar();
    pollRef.current = setInterval(() => carregar(true), 5000);
    return () => clearInterval(pollRef.current);
  }, [carregar]);

  // Scroll para o fim quando chegam novas mensagens
  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  const enviar = async () => {
    const conteudo = texto.trim();
    if (!conteudo || enviando) return;

    setEnviando(true);
    setErro('');
    try {
      const data = await apiFetch(`/chat/${reserva_id}`, 'POST', { conteudo });
      setMensagens(prev => [...prev, data.mensagem]);
      setTexto('');
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (e) {
      setErro(e.message || 'Erro ao enviar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const voltar = () => {
    if (utilizador?.tipo === 'fornecedor') navigate('/fornecedor/reservas');
    else navigate('/cliente/reservas');
  };

  if (carregando) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <MessageIcon size={32} color="var(--primary)" />
          <div style={{ marginTop: 12, fontSize: 14 }}>A carregar conversa…</div>
        </div>
      </div>
    );
  }

  const grouped = groupByDay(mensagens);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', maxHeight: '100vh', overflow: 'hidden' }}>

      {/* ── Header ─────────────────────────────────────────── */}
      <div style={{
        background: 'var(--card)',
        borderBottom: '1px solid var(--border)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexShrink: 0,
        zIndex: 10,
      }}>
        <button onClick={voltar}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}>
          <ArrowLeftIcon size={18} />
        </button>

        {/* Avatar outra parte */}
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: 'var(--primary)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, fontWeight: 700, flexShrink: 0, overflow: 'hidden',
        }}>
          {outraParte?.foto_url
            ? <img src={outraParte.foto_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
            : (outraParte?.nome || '?')[0].toUpperCase()
          }
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {outraParte?.nome || 'Conversa'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {anuncioTitulo} · {ESTADOS_LABEL[estado] || estado}
          </div>
        </div>

        {/* Indicador estado do chat */}
        {chatActivo
          ? <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--success)', background: 'var(--success-bg)', padding: '3px 8px', borderRadius: 20, flexShrink: 0 }}>
              <CheckIcon size={10} color="var(--success)" /> Activo
            </div>
          : <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-secondary)', padding: '3px 8px', borderRadius: 20, flexShrink: 0 }}>
              <LockIcon size={10} /> Bloqueado
            </div>
        }
      </div>

      {/* ── Banner de estado ──────────────────────────────── */}
      {!chatActivo && (
        <div style={{
          background: estado === 'pendente' ? 'var(--warning-bg)' : 'var(--bg-secondary)',
          border: `1px solid ${estado === 'pendente' ? 'var(--warning-border)' : 'var(--border)'}`,
          borderRadius: 10, margin: '12px 16px 0',
          padding: '10px 14px',
          display: 'flex', alignItems: 'flex-start', gap: 10,
          fontSize: 13,
          color: estado === 'pendente' ? 'var(--warning)' : 'var(--text-secondary)',
          flexShrink: 0,
        }}>
          <LockIcon size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            {estado === 'pendente'
              ? 'O chat é desbloqueado assim que o pagamento for confirmado.'
              : estado === 'concluida'
                ? 'Esta conversa foi desactivada após a conclusão da reserva e pagamento ao fornecedor.'
                : estado === 'cancelada'
                  ? 'Esta conversa foi desactivada porque a reserva foi cancelada.'
                  : 'Chat não disponível para esta reserva.'
            }
          </span>
        </div>
      )}

      {/* ── Área de mensagens ─────────────────────────────── */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}>
        {grouped.length === 0 && chatActivo && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', marginTop: 40 }}>
            <MessageIcon size={36} color="var(--border)" />
            <div style={{ marginTop: 10, fontSize: 14 }}>Nenhuma mensagem ainda.</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>Começa a conversa com {outraParte?.nome || 'a outra parte'}.</div>
          </div>
        )}

        {grouped.map(item => {
          if (item.type === 'separator') {
            return (
              <div key={item.id} style={{
                textAlign: 'center', fontSize: 11,
                color: 'var(--text-muted)', margin: '10px 0 6px',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                {item.label}
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
            );
          }

          const minha = item.remetente_id === utilizador?.id;

          return (
            <div key={item.id} style={{
              display: 'flex',
              justifyContent: minha ? 'flex-end' : 'flex-start',
              marginBottom: 4,
            }}>
              {/* Avatar (só para mensagens recebidas) */}
              {!minha && (
                <div style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'var(--primary)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                  marginRight: 7, marginTop: 2, overflow: 'hidden',
                  alignSelf: 'flex-end',
                }}>
                  {item.remetente_foto
                    ? <img src={item.remetente_foto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => e.target.style.display = 'none'} />
                    : (item.remetente_nome || '?')[0].toUpperCase()
                  }
                </div>
              )}

              <div style={{ maxWidth: '72%' }}>
                {!minha && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3, paddingLeft: 2 }}>
                    {item.remetente_nome}
                  </div>
                )}
                <div style={{
                  background: minha ? 'var(--primary)' : 'var(--card)',
                  color: minha ? '#fff' : 'var(--text-primary)',
                  border: minha ? 'none' : '1px solid var(--border)',
                  borderRadius: minha ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  padding: '8px 12px',
                  fontSize: 14,
                  lineHeight: 1.45,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                }}>
                  {item.conteudo}
                </div>
                <div style={{
                  fontSize: 10, color: 'var(--text-muted)',
                  marginTop: 3,
                  textAlign: minha ? 'right' : 'left',
                  paddingLeft: minha ? 0 : 2,
                  paddingRight: minha ? 2 : 0,
                  display: 'flex',
                  justifyContent: minha ? 'flex-end' : 'flex-start',
                  alignItems: 'center',
                  gap: 4,
                }}>
                  {formatHour(item.created_at)}
                  {minha && (
                    item.lida
                      ? <CheckIcon size={10} color="var(--primary)" />
                      : <ClockIcon size={10} color="var(--text-muted)" />
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div ref={fimRef} />
      </div>

      {/* ── Erro ao enviar ───────────────────────────────── */}
      {erro && (
        <div style={{
          background: 'var(--danger-bg)', border: '1px solid var(--danger-border)',
          borderRadius: 8, margin: '0 16px 8px',
          padding: '8px 12px', fontSize: 12,
          color: 'var(--danger)', display: 'flex', gap: 6, alignItems: 'center',
          flexShrink: 0,
        }}>
          <AlertIcon size={13} /> {erro}
          <button onClick={() => setErro('')}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* ── Input de mensagem ─────────────────────────────── */}
      <div style={{
        borderTop: '1px solid var(--border)',
        padding: '12px 16px',
        background: 'var(--card)',
        flexShrink: 0,
      }}>
        {chatActivo ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea
              ref={inputRef}
              value={texto}
              onChange={e => setTexto(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Escreve uma mensagem…"
              rows={1}
              style={{
                flex: 1,
                resize: 'none',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: 20,
                padding: '10px 14px',
                fontSize: 14,
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                outline: 'none',
                maxHeight: 120,
                overflowY: 'auto',
                lineHeight: 1.4,
              }}
              onInput={e => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
            />
            <button
              onClick={enviar}
              disabled={!texto.trim() || enviando}
              style={{
                width: 40, height: 40,
                borderRadius: '50%',
                background: texto.trim() && !enviando ? 'var(--primary)' : 'var(--border)',
                border: 'none', cursor: texto.trim() && !enviando ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: 'background 0.2s',
              }}>
              <SendIcon size={16} color="#fff" />
            </button>
          </div>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, padding: '8px 0',
            color: 'var(--text-muted)', fontSize: 13,
          }}>
            <LockIcon size={14} />
            Chat bloqueado
          </div>
        )}
      </div>
    </div>
  );
}
